import { useEffect, useState } from "react";
import moment, { Moment } from "moment";
import { MdArrowDropDown, MdCheck, MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { Column, Row } from "../container/Container";
import { TextBodyLarge, TextLabelLarge2 } from "../text/Texts";
import { cn } from "../ui/cn";
import { TransparentButton } from "../buttons/Buttons";

export interface CalendarInputProps {
	value?: Moment;
	onChange?: (value: Moment | undefined) => void;
	onClose?: () => void;
	minYear?: number;
	maxYear?: number;
	minDate?: Moment;
	maxDate?: Moment;
	showClearBtn?: boolean;
}

type DateUnit = 'day' | 'month' | 'year';

export const calcMaxDate = ({ maxDate, maxYear }: { maxDate?: Moment, maxYear?: number }) => {
	return maxDate ?? (maxYear !== undefined
		? moment().year(maxYear).endOf('year')
		: moment().endOf('day'));
}

export const calcMinDate = ({ maxDate, minDate, minYear }: { maxDate: Moment, minDate?: Moment, minYear?: number }) => {
	return minDate ?? (minYear !== undefined
		? maxDate.clone().year(minYear).startOf('year')
		: maxDate.clone().subtract(100, 'years').startOf('day'));
}

export function CalendarInput({
	value, onChange, onClose,
	minYear, maxYear,
	minDate, maxDate,
	showClearBtn = true,
}: CalendarInputProps) {
	const [mode, setMode] = useState<DateUnit>('day');
	const [internalValue, setInternalValue] = useState<Moment>(value || moment().year(2026).startOf('year'));

	maxDate = calcMaxDate({ maxDate, maxYear });
	minDate = calcMinDate({ maxDate, minDate, minYear });

	const today = moment().startOf('day');
	const years = [...Array(maxDate.year() - minDate.year() + 1)].map((_, i) => minDate.year() + i);
	const months = moment.months();
	const days = [...Array(42)].map((_, i) => {
		const monthStart = internalValue.clone().startOf('month');
		const calendarStart = monthStart.clone().startOf('week');

		if (monthStart.day() === 0)
			calendarStart.subtract(1, 'week');

		return calendarStart.clone().add(i, 'days');
	});

	const cellCommonCls = 'h-10 w-10 m-1 flex items-center justify-center rounded-[100px] text-onSurface font-poppins text-body-large';

	useEffect(() => {
		if (!value) return;
		setInternalValue(clampDate(value));
	}, [value]);

	const clampDate = (date: Moment) => {
		let clamped = date.clone();
		if (clamped.isBefore(minDate)) clamped = minDate.clone();
		else if (clamped.isAfter(maxDate)) clamped = maxDate.clone();
		return clamped;
	};

	const setDateInRange = (date: Moment) => {
		const newDate = isDateInRange(date) ? date : clampDate(date);
		setInternalValue(newDate);
	}

	const isDateInRange = (date: Moment) => {
		return date.isSameOrAfter(minDate, 'day')
			&& date.isSameOrBefore(maxDate, 'day');
	};

	const isPeriodInRange = (date: Moment, dateUnit: DateUnit) => {
		const start = date.clone().startOf(dateUnit);
		const end = date.clone().endOf(dateUnit);
		return end.isSameOrAfter(minDate, 'day')
			&& start.isSameOrBefore(maxDate, 'day');
	};

	const handleClear = () => onChange?.(undefined);
	const handleClose = () => onClose?.()
	const handleOk = () => {
		if (!value) return;
		onChange?.(internalValue);
		handleClose();
	}

	const handleSelect = (date: Moment) => {
		onChange?.(date.clone());
	}

	const headerSection = (dateUnit: DateUnit) => {
		const label = internalValue.format(dateUnit === 'month' ? 'MMM' : 'YYYY');

		const targetLeft = internalValue.clone().subtract(1, dateUnit);
		const targetRight = internalValue.clone().add(1, dateUnit);
		const canGoLeft = isPeriodInRange(targetLeft, dateUnit);
		const canGoRight = isPeriodInRange(targetRight, dateUnit);

		return (
			<Row>
				<button
					disabled={!canGoLeft}
					onClick={() => setDateInRange(targetLeft)}
					className={cn('h-12 w-12 p-3', !canGoLeft && 'opacity-50')}
					children={(<MdKeyboardArrowLeft size={24} className="text-onSurfaceVariant" />)}
				/>
				<button
					onClick={() => setMode(mode === dateUnit ? 'day' : dateUnit)}
					className="flex flex-row gap-2 pl-2 pr-1 items-center"
				>
					<TextLabelLarge2 text={label} className="w-10 text-onSurfaceVariant" />
					<MdArrowDropDown size={18} className="text-onSurfaceVariant" />
				</button>
				<button
					disabled={!canGoRight}
					onClick={() => setDateInRange(targetRight)}
					className={cn('h-12 w-12 p-3', !canGoRight && 'opacity-50')}
					children={(<MdKeyboardArrowRight size={24} className="text-onSurfaceVariant" />)}
				/>
			</Row>
		);
	}

	const renderList = () => {
		const items = mode === 'month' ? months : years;
		const itemEls = items.map((item, index) => {
			const period = mode === 'month' ? index : item as number;
			// Always returns Moment but TS can't infer due to method overloads
			const target = internalValue.clone()[mode](period) as Moment;
			const isSelected = internalValue[mode]() === period;
			const isDisabled = !isPeriodInRange(target, mode)

			return (
				<Row
					key={index}
					onClick={() => {
						if (isDisabled) return;
						setDateInRange(target);
						setMode('day');
					}}
					className={cn(
						'h-12 gap-4 px-4 items-center cursor-pointer duration-200 hover:bg-[#D5D9DB]',
						isSelected && 'bg-[#D5D9DB]',
						isDisabled && 'pointer-events-none opacity-50',
					)}
				>
					<MdCheck size={24} className={cn('text-onSurfaceVariant', !isSelected && 'invisible')} />
					<TextBodyLarge text={item.toString()} className="text-onSurface" />
				</Row>
			);
		});

		return (
			<div className="h-[428px] pr-1 blue-scroll pt-2 pb-5">
				<Column className="h-[400px] pr-1 overflow-y-auto no-shrink">
					{itemEls}
				</Column>
			</div>
		);
	};

	return (
		<div className="w-[360px] rounded-[16px] bg-surfaceContainerHigh select-none">
			<Row className={cn('h-[64px] gap-3 items-center', mode !== 'day' && 'border-b border-b-accent60')}>
				{headerSection('month')}
				{headerSection('year')}
			</Row>

			{mode === 'day' && (
				<>
					<div className="grid grid-cols-7 px-3 pb-1">
						{moment.weekdays().map(w => (<div key={w} children={w[0]} className={cellCommonCls} />))}
						{days.map((date, i) => {
							const monthDay = date.date();
							const isCurrentMonth = date.isSame(internalValue, 'month');
							const isToday = date.isSame(today, 'day');
							const isSelected = value && date.isSame(internalValue, 'day');

							return (
								<div
									key={i}
									children={monthDay}
									onClick={() => handleSelect(date)}
									className={cn(
										cellCommonCls,
										!isCurrentMonth && 'text-onSurfaceVariant',
										isToday && 'shadow-[inset_0_0_0_1px_theme(colors.onSurface)]',
										isSelected && 'text-white bg-primary',
										'hover:text-white hover:bg-primary cursor-pointer',
										!isDateInRange(date) && 'opacity-50 pointer-events-none',
									)}
								/>
							);
						})}
					</div>

					<Row className="px-3 py-2 justify-between">
						<TransparentButton
							label="Clear"
							onClick={handleClear}
							className={cn(!showClearBtn && 'invisible')}
						/>
						<Row className="gap-2">
							<TransparentButton label="Cancel" onClick={handleClose} />
							<TransparentButton label="OK" onClick={handleOk} />
						</Row>
					</Row>
				</>
			)}
			{(mode === 'month' || mode === 'year') && renderList()}
		</div>
	);
}
